const STORAGE_KEY = 'motto_concurrency_device_id';

/**
 * Generates a UUID v4
 */
const generateUUID = (): string => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  // Fallback for older browsers
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

/**
 * Gets or creates a device identifier.
 *
 * @param clientProvidedId - Optional ID provided by the client (e.g., custom device ID)
 * @returns The device ID to use for concurrency tracking
 *
 * Priority:
 * 1. If clientProvidedId is provided, use it directly (client manages it)
 * 2. Otherwise, retrieve from localStorage (shared across tabs)
 * 3. If nothing in localStorage, generate a new UUID and store it
 */
export const getDeviceId = (clientProvidedId?: string): string => {
  // If client provides an ID, use it directly (don't store - client manages it)
  // let deviceId = localStorage.getItem(STORAGE_KEY);
  if (clientProvidedId) {
    localStorage.setItem(STORAGE_KEY, clientProvidedId);
    return clientProvidedId;
  }

  try {
    // Get from localStorage or generate new
    let deviceId = localStorage.getItem(STORAGE_KEY);

    if (!deviceId) {
      deviceId = `device_${generateUUID()}`;
      localStorage.setItem(STORAGE_KEY, deviceId);
    }

    return deviceId;
  } catch {
    // localStorage may be blocked (e.g., in incognito mode)
    return `device_${generateUUID()}`;
  }
};

/**
 * Clears the stored device ID from localStorage
 * (useful for testing or user logout scenarios)
 */
export const clearDeviceId = (): void => {
  if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
    return;
  }

  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Ignore errors
  }
};
